import BrowseListSchema from '../../features/browse/BrowseListSchema';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { SITE_URL } from '../../utils/env';
import React, { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import BrowseShell from '../../features/browse/BrowseShell';
import BrowsePlaylistCard, { playlistTo } from '../../features/browse/BrowsePlaylistCard';
import { selectFeaturedPlaylist, selectCreators } from '../../features/browse/playlistSelection';
import { BrowseInlineAd } from '../../features/browse/BrowseSponsored';
import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import { planSponsoredInsertions } from '../../features/ads/placement';
import { AccountBoundary } from '../../features/settings/shared';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import { uniquePlaylists, validatePlaylistPage, nextPlaylistPage } from './playlistListModel';
import PlaylistListState from './PlaylistListState';
import DiscoverySection from '../../app-ui/AppContentSection';
import baseline, { playlistText, appTextStyle } from '../../app-ui/baseline';
import './PlaylistApp.css';
import ContentShelf, { ShelfNavigationContext } from '../../components/responsive/ContentShelf';
import ProfileIdentity from '../../components/content/ProfileIdentity';
import PlainImage from '../../features/images/PlainImage';
import Chevron from '../../components/content/Chevron';
import { playlistApi } from '../../api/playlist';
import { usersApi } from '../../api/users';
import { meApi } from '../../api/me';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE } from '../../constants/gaEnums';
import SEO from '../../components/common/SEO';
export default function Playlists() {
  return <AccountBoundary component={PlaylistsBoundary} />;
}
function PlaylistsBoundary({ user }) {
  const { locale } = useWebLocale();
  const memory = useBrowseSession('playlists');
  if (useUserStore.getState().user?.id !== user?.id) return null;
  return (
    <PlaylistsView key={`${locale}:${memory.entry.version}:${memory.location.key}`} {...memory} />
  );
}
function PlaylistsView({ entry, returning }) {
  const { locale, t } = useWebLocale(),
    en = locale === 'en';
  const text = (key, params, compact) => playlistText(locale, key, params, compact);
  const navigate = useNavigate(),
    user = useUserStore((s) => s.user);
  const [limit, setLimit] = useState(entry.playlistLimit || 25);
  entry.playlistLimit = limit;
  const identity = [entry.version, locale];
  const queryOptions = {
    retry: false,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  };
  const loadPage = async (sort, page, size, signal) =>
    validatePlaylistPage(
      await playlistApi.getAllPlaylists(sort, page, size, { signal, timeout: 15000 }),
      page,
    );
  const tablet = useMediaQuery('(min-width:768px)');
  const desktop = useMediaQuery('(min-width:1200px)');
  const newestCount = 3;
  const moreColumns = desktop ? 3 : tablet ? 2 : 1;
  const featuredQuery = useQuery({
    ...queryOptions,
    queryKey: ['browse-playlists', 'popular', 20, identity],
    queryFn: ({ signal }) => loadPage('popular', 1, 20, signal),
  });
  const latest = useInfiniteQuery({
    ...queryOptions,
    queryKey: ['browse-playlists', 'latest', 28, identity],
    queryFn: ({ pageParam, signal }) => loadPage('latest', pageParam, 28, signal),
    initialPageParam: 1,
    getNextPageParam: nextPlaylistPage,
  });
  const mine = useQuery({
    ...queryOptions,
    queryKey: ['browse-my-playlists', user?.id, identity],
    queryFn: () => meApi.getMyLists(),
    enabled: Boolean(user?.id),
  });
  const featured = useMemo(() => featuredQuery.data?.data || [], [featuredQuery.data]);
  const playlists = useMemo(
    () => uniquePlaylists(latest.data?.pages.flatMap((page) => page.data) || []),
    [latest.data],
  );
  const spotlight = useMemo(
    () => selectFeaturedPlaylist(featured, user?.nickname),
    [featured, user?.nickname],
  );
  const curatorQuery = useQuery({
    ...queryOptions,
    queryKey: ['browse-curator', spotlight?.username, identity],
    queryFn: () => usersApi.getUserLists(spotlight.username),
    enabled: Boolean(spotlight?.username),
  });
  const curator = curatorQuery.data?.data || [];
  const curatorFallback = uniquePlaylists([...featured, ...playlists]).filter(
    (item) => item.username === spotlight?.username,
  );
  // Mount the rail after both candidate pools resolve. Prepending the second response
  // into a visible scroll-snap rail can anchor it at its last member.
  const creators =
    featuredQuery.isPending || latest.isPending
      ? []
      : selectCreators(featured, playlists, spotlight?.username);
  const campaigns = useSponsoredPlacements('PLAYLISTS_FEED').filter(
    (campaign) => campaign.placements?.[0]?.format === 'INLINE_BANNER',
  );
  const visibleLimit = Math.ceil(limit / moreColumns) * moreColumns;
  const remaining = playlists.slice(newestCount, newestCount + visibleLimit),
    ads = planSponsoredInsertions(campaigns, remaining.length).map((slot) => ({
      ...slot,
      at: Math.min(remaining.length, Math.ceil(slot.at / moreColumns) * moreColumns),
    }));
  const hasMore = playlists.length > newestCount + visibleLimit || latest.hasNextPage;
  const { hasNextPage, isFetching, isError, fetchNextPage } = latest;
  useEffect(() => {
    if (playlists.length - newestCount < visibleLimit && hasNextPage && !isFetching && !isError)
      fetchNextPage({ cancelRefetch: false });
  }, [
    playlists.length,
    newestCount,
    visibleLimit,
    hasNextPage,
    isFetching,
    isError,
    fetchNextPage,
  ]);
  const position = useBrowsePosition({
    entry,
    returning,
    ready:
      !latest.isPending &&
      !latest.isFetching &&
      !featuredQuery.isPending &&
      !curatorQuery.isFetching,
  });
  useEffect(() => {
    const root = position.ref.current;
    const rail = root?.querySelector('.pp-shelf-track');
    if (!rail) return;
    if (returning) rail.scrollLeft = entry.playlistRail || 0;
    const save = () => {
      if (entry.current()) entry.playlistRail = rail.scrollLeft;
    };
    rail.addEventListener('scroll', save, { passive: true });
    return () => rail.removeEventListener('scroll', save);
  }, [entry, returning, creators.length, position.ref]);
  useTrackPageView({ pageType: PAGE_TYPE.LISTS_MAIN });
  const create = () =>
    user?.id ? navigate('/lists/create?from=list') : saveCurrentPathAndRedirectToLogin(navigate);
  const created = Boolean(mine.data?.data?.length);
  return (
    <BrowseShell active="playlists">
      <SEO title={t('playlists')} description={SEO_DESCRIPTIONS.LISTS} url={`${SITE_URL}/lists`} />
      <BrowseListSchema
        title={t('playlists')}
        items={playlists.map((item) => ({
          title: item.title,
          image: item.imageUrl,
          to: playlistTo(item),
        }))}
      />
      <ShelfNavigationContext.Provider value="edges">
        <div
          className="pp-app-playlist-content"
          {...position}
          style={{
            '--app-section-gap': `${baseline.playlist.content.gap}px`,
            '--app-row-min-height': `${baseline.playlist.playlistRow.minHeight}px`,
          }}
        >
          <button className="pp-playlist-create" onClick={create}>
            <span className="pp-playlist-plus">+</span>
            <span>
              <strong>{text(created ? 'createNew' : 'createFirst')}</strong>
              <small>{text(created ? 'createNewBody' : 'createFirstBody')}</small>
            </span>
            <Chevron />
          </button>
          <div className="pp-playlist-intro-grid">
            <PlaylistListState query={featuredQuery}>
              {spotlight && (
                <DiscoverySection title={text('curatorSpotlight')}>
                  <BrowsePlaylistCard
                    item={spotlight}
                    curator
                    curatorCount={curatorQuery.isSuccess ? curator.length : curatorFallback.length}
                    curatorSaves={(curatorQuery.isSuccess ? curator : curatorFallback).reduce(
                      (sum, item) => sum + (item.bookmarkCount || 0),
                      0,
                    )}
                  />
                </DiscoverySection>
              )}
            </PlaylistListState>
            {creators.length > 0 && (
              <DiscoverySection title={text('recommendedCurators')}>
                <ContentShelf profile="members" label={text('recommendedCurators')}>
                  {creators.map((item) => (
                    <Link
                      key={item.username}
                      to={`/users/${encodeURIComponent(item.username)}`}
                      className="pp-playlist-creator"
                    >
                      <span className="pp-playlist-creator-avatar">
                        {item.userProfileImageUrl?.trim() ? (
                          <PlainImage src={item.userProfileImageUrl} alt="" loading="lazy" />
                        ) : (
                          item.username.slice(0, 1)
                        )}
                      </span>
                      <span className="pp-playlist-creator-copy">
                        <ProfileIdentity
                          name={item.username}
                          mark={item.userProfileMark}
                          avatar={false}
                        />
                        <small style={appTextStyle(baseline.contentText.metadata)}>
                          {text('representativeSaves', { count: item.bookmarkCount || 0 }, true)}
                        </small>
                      </span>
                    </Link>
                  ))}
                </ContentShelf>
              </DiscoverySection>
            )}
          </div>
          <DiscoverySection title={text('new')}>
            <PlaylistListState query={latest} hideNextError>
              {!playlists.length && (
                <div className="pp-browse-empty">
                  <strong>{text('emptyTitle')}</strong>
                  <p>{text('emptyBody')}</p>
                </div>
              )}
              <div className="pp-playlist-new-grid">
                {playlists.slice(0, newestCount).map((item) => (
                  <BrowsePlaylistCard key={item.id} item={item} />
                ))}
              </div>
            </PlaylistListState>
          </DiscoverySection>
          {featured.length > 0 && (
            <DiscoverySection title={text('popular')} subtitle={text('byViews')}>
              <div className="pp-playlist-popular-grid">
                <BrowsePlaylistCard item={featured[0]} hero />
                <div className="pp-playlist-popular-runners">
                  {featured.slice(1, 3).map((item, index) => (
                    <BrowsePlaylistCard key={item.id} item={item} rank={index + 2} />
                  ))}
                </div>
              </div>
            </DiscoverySection>
          )}
          {(remaining.length > 0 || latest.isFetchNextPageError) && (
            <DiscoverySection title={text('more')}>
              {latest.isFetchNextPageError && <p role="alert">{text('listLoadError')}</p>}
              <div className="pp-playlist-more-grid">
                {Array.from({ length: remaining.length + 1 }, (_, index) => (
                  <React.Fragment key={index}>
                    {ads
                      .filter((slot) => slot.at === index)
                      .map((slot) => (
                        <div
                          className="pp-browse-full"
                          key={`${slot.campaign.id}-${slot.occurrence}`}
                        >
                          <BrowseInlineAd campaign={slot.campaign} />
                        </div>
                      ))}
                    {remaining[index] && <BrowsePlaylistCard item={remaining[index]} />}
                  </React.Fragment>
                ))}
              </div>
              {(hasMore || latest.isFetchNextPageError) && (
                <button
                  className="pp-action pp-playlist-more"
                  disabled={latest.isFetching}
                  onClick={() =>
                    latest.isFetchNextPageError
                      ? latest.fetchNextPage({ cancelRefetch: false })
                      : setLimit((count) => count + 25)
                  }
                >
                  {latest.isFetching
                    ? en
                      ? 'Loading…'
                      : '불러오는 중…'
                    : latest.isFetchNextPageError
                      ? t('retry')
                      : en
                        ? 'Show more'
                        : '더보기'}
                </button>
              )}
              {limit > 25 && !hasMore && !latest.isFetchNextPageError && (
                <button className="pp-action pp-playlist-more" onClick={() => setLimit(25)}>
                  {en ? 'Show less' : '접기'}
                </button>
              )}
            </DiscoverySection>
          )}
        </div>
      </ShelfNavigationContext.Provider>
    </BrowseShell>
  );
}
